import type { CreatorActivity, UpcomingPolicyStep } from "@unbox/api";
import {
  Announcement03,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Hand,
  MessageTextSquare01,
  PackageCheck,
  Send01,
  Truck01,
  UserSquare,
  VideoRecorder,
  VideoRecorderOff,
  XClose,
} from "@untitledui/icons";
import { useState } from "react";
import { cx } from "@/utils/cx";
import { extractTextMessageContent } from "@/utils/message-content";
import { getUserInitials } from "@/utils/user-initials";
import {
  CollaborationActivityCard,
  CollaborationCard,
  type CollaborationWithProducts,
  ImageMessage,
  ProductCard,
  renderTextWithLinks,
  SampleActivityCard,
  SimplifiedVideoCard,
} from "./activity-components";

export interface CollapsibleActivityItemProps {
  activity: CreatorActivity;
  creatorAvatar?: string | null;
  brandAvatar?: string | null;
  creatorName?: string | null;
  showConnectorLine?: boolean;
  leadingStep?: UpcomingPolicyStep;
  cardClassName?: string;
  fullWidth?: boolean;
}

const getLeadingActivityTitle = (step: UpcomingPolicyStep): string => {
  switch (step.type) {
    case "remove_from_collab":
      return "Remove from collaboration";
    default:
      return "Message creator";
  }
};

// Helper to get activity title
const getActivityTitle = (activity: CreatorActivity): string => {
  switch (activity.type) {
    case "message":
      // Special handling for collaboration invite messages
      if (activity.messageBody.type === "TARGET_COLLABORATION_CARD") {
        return "Sent Invite to Collaborate";
      }
      return "Message";
    case "video_post":
      return "Video Posted";
    case "collab_join":
      return "Accepted Collaboration";
    case "collab_removal":
      return "Removed from Collaboration";
    case "safety_event":
      if (activity.actionType === "video_removed") {
        return "Video Removed";
      }
      return "Video Demonetized";
    case "sample":
      if (activity.displayStatus === "approved") {
        return "Sample Approved";
      }
      if (activity.displayStatus === "rejected") {
        return "Sample Rejected";
      }
      if (activity.displayStatus === "in_transit") {
        return "Sample in Transit";
      }
      if (activity.displayStatus === "delivered") {
        return "Sample Delivered";
      }
      return "Sample Activity";
    case "gmv_max":
      if (activity.actionType === "video_added_to_gmv_max") {
        return "Video added to GMV Max campaign";
      }
      return "Video removed from GMV Max campaign";
    default:
      return "Activity";
  }
};

// Helper to get activity icon
const getActivityIcon = (activity: CreatorActivity): React.ReactNode => {
  switch (activity.type) {
    case "message":
      // Special handling for collaboration invite messages
      if (activity.messageBody.type === "TARGET_COLLABORATION_CARD") {
        return <Announcement03 className="w-6 h-6 text-gray-400" strokeWidth={1.5} />;
      }
      return <MessageTextSquare01 className="w-6 h-6 text-gray-400" strokeWidth={1.5} />;
    case "video_post":
      return <VideoRecorder className="w-6 h-6 text-gray-400" strokeWidth={1.5} />;
    case "collab_join":
      return <Check className="w-6 h-6 text-gray-400" strokeWidth={1.5} />;
    case "collab_removal":
      return <XClose className="w-6 h-6 text-gray-400" strokeWidth={1.5} />;
    case "safety_event":
      return <VideoRecorderOff className="w-6 h-6 text-gray-400" strokeWidth={1.5} />;
    case "sample":
      if (activity.displayStatus === "approved") {
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M20.5 12.8704V8.11111C20.5 7.19064 19.7389 6.44444 18.8 6.44444C17.8611 6.44444 17.1 7.19064 17.1 8.11111M6.9 11.4444V14.2222M6.9 11.4444V4.77778C6.9 3.8573 7.66112 3.11111 8.6 3.11111C9.53888 3.11111 10.3 3.8573 10.3 4.77778M6.9 11.4444C6.9 10.524 6.13888 9.77778 5.2 9.77778C4.26112 9.77778 3.5 10.524 3.5 11.4444V13.6667C3.5 18.269 7.30558 22 12 22C12.6934 22 13.3674 21.9186 14.0126 21.765M10.3 4.77778V10.8889M10.3 4.77778V3.66667C10.3 2.74619 11.0611 2 12 2C12.9389 2 13.7 2.74619 13.7 3.66667V4.77778M13.7 4.77778V10.8889M13.7 4.77778C13.7 3.8573 14.4611 3.11111 15.4 3.11111C16.3389 3.11111 17.1 3.8573 17.1 4.77778V8.11111M17.1 8.11111V10.8889"
              stroke="#9CA3AF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15.5 18L17.5 20L21.5 16"
              stroke="#9CA3AF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15.5 18L17.5 20L21.5 16"
              stroke="#9CA3AF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      }
      if (activity.displayStatus === "rejected") {
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path
              d="M20.5 12.7579V8.11111C20.5 7.19064 19.7389 6.44444 18.8 6.44444C17.8611 6.44444 17.1 7.19064 17.1 8.11111M12 22C7.30558 22 3.5 18.269 3.5 13.6667V11.4444C3.5 10.524 4.26112 9.77778 5.2 9.77778C6.13888 9.77778 6.9 10.524 6.9 11.4444M6.9 11.4444V14.2222M6.9 11.4444V4.77778C6.9 3.8573 7.66112 3.11111 8.6 3.11111C9.53888 3.11111 10.3 3.8573 10.3 4.77778M10.3 4.77778V10.8889M10.3 4.77778V3.66667C10.3 2.74619 11.0611 2 12 2C12.9389 2 13.7 2.74619 13.7 3.66667V4.77778M13.7 4.77778V10.8889M13.7 4.77778C13.7 3.8573 14.4611 3.11111 15.4 3.11111C16.3389 3.11111 17.1 3.8573 17.1 4.77778V8.11111M17.1 8.11111V10.8889"
              stroke="#9CA3AF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16.5 16.0001L21.5 21.0001M21.5 16.0001L16.5 21.0001"
              stroke="#9CA3AF"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );
      }
      if (activity.displayStatus === "in_transit") {
        return <Truck01 className="w-5 h-5 text-gray-400" strokeWidth={1.5} />;
      }
      if (activity.displayStatus === "delivered") {
        return <PackageCheck className="w-5 h-5 text-gray-400" strokeWidth={1.5} />;
      }
      return <Hand className="w-5 h-5 text-gray-400" strokeWidth={1.5} />;
    case "gmv_max":
      if (activity.actionType === "video_added_to_gmv_max") {
        return <Eye className="w-5 h-5 text-gray-400" strokeWidth={1.5} />;
      }
      return <EyeOff className="w-5 h-5 text-gray-400" strokeWidth={1.5} />;
    default:
      return <UserSquare className="w-5 h-5 text-gray-400" strokeWidth={1.5} />;
  }
};

// Helper to format time in @HH:MMpm format
const formatTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "pm" : "am";
  const displayHours = hours % 12 || 12;
  const displayMinutes = minutes.toString().padStart(2, "0");
  return `@${displayHours}:${displayMinutes}${ampm}`;
};

// Helper to get message preview
const getMessagePreview = (activity: CreatorActivity, maxLength?: number): string | null => {
  if (activity.type !== "message") return null;

  // Don't show preview for collaboration invites
  if (activity.messageBody.type === "TARGET_COLLABORATION_CARD") {
    return null;
  }

  let preview = "";

  if (activity.messageBody.type === "TEXT") {
    preview = extractTextMessageContent(activity.messageBody.content);
  } else if (activity.messageBody.type === "IMAGE") {
    preview = "[Image]";
  } else if (activity.messageBody.type === "PRODUCT_CARD") {
    preview = "[Product Card]";
  } else {
    preview = "[Message]";
  }

  if (maxLength !== undefined && preview.length > maxLength) {
    return `${preview.substring(0, maxLength)}...`;
  }

  return preview;
};

// Determine if activity was initiated by creator or brand
const isCreatorInitiated = (activity: CreatorActivity): boolean => {
  // Messages: check direction
  if (activity.type === "message") {
    return activity.direction === "received";
  }

  // Video posts: always creator
  if (activity.type === "video_post") {
    return true;
  }

  // Collaborations: check initiatedBy field or default logic
  if (activity.type === "collab_join") {
    return true; // Creator accepted
  }

  if (activity.type === "collab_removal") {
    return false; // Brand removed
  }

  // Samples: check status/action
  if (activity.type === "sample") {
    return activity.initiatedBy === "creator";
  }

  // GMV Max: typically brand invites
  if (activity.type === "gmv_max") {
    return activity.initiatedBy === "creator";
  }

  // Safety events: typically system/brand
  if (activity.type === "safety_event") {
    return false;
  }

  return false;
};

// Helper to render simplified message content (without header, icon, sender, date)
const renderSimplifiedMessageContent = (activity: CreatorActivity): React.ReactNode => {
  if (activity.type !== "message") {
    return null;
  }

  const { messageBody, product, collaboration, image } = activity;

  // Product card
  if (messageBody.type === "PRODUCT_CARD" && product) {
    return <ProductCard product={product} />;
  }

  // Collaboration card
  if (messageBody.type === "TARGET_COLLABORATION_CARD" && collaboration) {
    return <CollaborationCard collaboration={collaboration as CollaborationWithProducts} />;
  }

  // Image
  if (messageBody.type === "IMAGE" && image) {
    return <ImageMessage image={image} />;
  }

  // Text message
  return (
    <div className="rounded-lg text-sm whitespace-pre-wrap bg-white text-primary [&_a]:text-brand-600">
      {renderTextWithLinks(extractTextMessageContent(messageBody.content))}
    </div>
  );
};

export const CollapsibleActivityItem = ({
  activity,
  creatorAvatar,
  brandAvatar,
  creatorName,
  showConnectorLine = false,
  leadingStep,
  cardClassName,
  fullWidth = false,
}: CollapsibleActivityItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isCreator = isCreatorInitiated(activity);
  let activityTitle = getActivityTitle(activity);
  if (leadingStep) {
    activityTitle = getLeadingActivityTitle(leadingStep);
  }
  const timeString = formatTime(activity.timestamp);
  const messagePreview = getMessagePreview(activity, fullWidth ? undefined : 40);
  const activityIcon = getActivityIcon(activity);

  return (
    <div className={cx("relative flex gap-3", fullWidth ? "w-full" : "max-w-[550px]")}>
      {/* Avatar with Timeline */}
      <div className="flex flex-col items-center flex-shrink-0">
        {/* Avatar */}
        <div className="relative z-10">
          {leadingStep ? (
            <span className="flex size-10 items-center justify-center rounded-full border border-dashed border-utility-blue-light-300 bg-utility-blue-light-50">
              <Send01 className="size-[18px] text-utility-blue-light-500" />
            </span>
          ) : isCreator && creatorAvatar ? (
            <img src={creatorAvatar} alt="Creator" className="w-10 h-10 rounded-full object-cover" />
          ) : isCreator ? (
            <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center">
              <span className="text-sm font-semibold text-primary">{getUserInitials(creatorName || "")}</span>
            </div>
          ) : !isCreator && brandAvatar ? (
            <img src={brandAvatar} alt="Brand" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-sm font-semibold text-gray-700">B</span>
            </div>
          )}
        </div>

        {/* Connector Line */}
        {showConnectorLine && <div className="w-px bg-gray-300 flex-1 min-h-[40px]" />}
      </div>

      {/* Activity Card - Collapsible */}
      <div className="flex-1 pb-4">
        {/* Collapsed Card */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={cx(
            "w-full flex items-center gap-2 px-2 py-2.5 bg-white border border-border-primary hover:bg-secondary transition-colors",
            isExpanded && activity.type === "message" && activity.messageBody.type !== "TARGET_COLLABORATION_CARD"
              ? "rounded-t-lg border-b-0" // Hide border for regular messages
              : isExpanded &&
                  (activity.type === "message" ||
                    activity.type === "sample" ||
                    activity.type === "video_post" ||
                    activity.type === "safety_event" ||
                    activity.type === "gmv_max" ||
                    activity.type === "collab_join" ||
                    activity.type === "collab_removal")
                ? "rounded-t-lg" // Keep border for collaboration invites and other activities
                : "rounded-lg", // Default for collapsed
            cardClassName,
          )}
        >
          {/* Left Column: Activity Icon */}
          <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center">
            {activityIcon || <UserSquare className="w-5 h-5 text-gray-400" />}
          </div>

          {/* Middle Column: Activity Info */}
          <div className="flex-1 min-w-0 flex flex-col gap-0.5 justify-center items-start">
            {/* Activity Type */}
            <span className="text-sm font-medium text-primary">{activityTitle}</span>

            {/* Time + Message Preview */}
            <div className="flex max-w-full items-center gap-2 text-xs text-tertiary">
              <span className="whitespace-nowrap">{timeString}</span>
              {messagePreview && (
                <>
                  <span className="text-border-primary">·</span>
                  <span className="truncate">{messagePreview}</span>
                </>
              )}
            </div>
          </div>

          {/* Right Column: Chevron Icon */}
          <ChevronDown
            className={cx("w-7 h-7 text-quaternary transition-transform flex-shrink-0", isExpanded && "rotate-180")}
          />
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div
            className={cx(
              activity.type === "message" && activity.messageBody.type !== "TARGET_COLLABORATION_CARD"
                ? // For regular messages: flows from header as one unified card (no top border, no border between header and body)
                  "p-4 pt-2 bg-white border border-t-0 border-border-primary rounded-b-lg"
                : activity.type === "message" ||
                    activity.type === "sample" ||
                    activity.type === "video_post" ||
                    activity.type === "safety_event" ||
                    activity.type === "gmv_max" ||
                    activity.type === "collab_join" ||
                    activity.type === "collab_removal"
                  ? // For collaboration invites, video activities and collaborations: flows from header with border separation
                    "bg-white border border-border-primary rounded-b-lg border-t-0"
                  : // For other activities: separate card with border
                    "mt-2 border border-border-primary rounded-lg bg-white p-4",
              cardClassName,
            )}
          >
            {activity.type === "message" ? (
              // Simplified message content - only the message body
              renderSimplifiedMessageContent(activity)
            ) : activity.type === "sample" ? (
              // Simplified sample card - product info
              <SampleActivityCard activity={activity} />
            ) : activity.type === "video_post" || activity.type === "safety_event" || activity.type === "gmv_max" ? (
              // Simplified video card for all video-related activities
              <SimplifiedVideoCard
                videoId={activity.videoId}
                videoTitle={activity.videoTitle}
                thumbnail={activity.thumbnail}
                safetyStatus={activity.safetyStatus}
                gmv={activity.gmv}
                views={activity.views}
                likes={activity.likes}
                sales={activity.sales}
              />
            ) : activity.type === "collab_join" ? (
              // Collaboration card for accepted collaboration (hide collaboration type)
              <CollaborationActivityCard activity={activity} showCollaborationType={false} />
            ) : activity.type === "collab_removal" ? (
              // Collaboration card for removed from collaboration (show collaboration type)
              <CollaborationActivityCard activity={activity} showCollaborationType={true} />
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};
